import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { useEnrollments, useMe, useAllActiveSessions, useDeleteSession, useShareStats, useUnenrollSection, type StudySession } from '../lib/api';
import { motion, AnimatePresence, springs, Pressable, FadeIn, Stagger, PageTransition, Modal, CountUp } from '../lib/motion';
import { haptic } from '../lib/haptics';

export function ModulesPage() {
  const { data: me } = useMe();
  const { data: enrollments } = useEnrollments();
  const { data: sessionsData } = useAllActiveSessions();
  const { data: shareStatsData } = useShareStats();
  const deleteSession = useDeleteSession();
  const unenroll = useUnenrollSection();
  const navigate = useNavigate();
  const [showAllSessions, setShowAllSessions] = useState(false);
  const [classSearch, setClassSearch] = useState('');
  const [confirmUnenrollId, setConfirmUnenrollId] = useState<string | null>(null);

  const hasClasses = (enrollments?.enrollments?.length ?? 0) > 0;
  const [dismissedWelcome, setDismissedWelcome] = useState(false);
  const showWelcome = enrollments && !hasClasses && !dismissedWelcome;

  const stats = me?.stats;
  const dailyGoal = 30;
  const todayDone = stats?.questionsToday ?? 0;
  const dailyProgress = Math.min(100, Math.round((todayDone / dailyGoal) * 100));
  const dailyDone = todayDone >= dailyGoal;
  const streak = stats?.currentStreak ?? 0;

  const sessions = sessionsData?.sessions ?? [];
  const totalShares = stats?.totalShares ?? shareStatsData?.total_shares ?? 0;
  const sharedToday = stats?.sharedToday ?? shareStatsData?.shared_today ?? 0;
  const totalAnswers = shareStatsData?.total_answers ?? 0;
  const correctPct = shareStatsData?.correct_percentage ?? 0;

  const [expandedPanel, setExpandedPanel] = useState<'shares' | 'daily' | null>(null);
  const togglePanel = (panel: 'shares' | 'daily') => setExpandedPanel(prev => prev === panel ? null : panel);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const handleConfirmDelete = () => {
    if (confirmDeleteId) {
      deleteSession.mutate(confirmDeleteId);
      setConfirmDeleteId(null);
    }
  };

  // Filter classes by search
  const allClasses = enrollments?.enrollments ?? [];
  const filteredClasses = useMemo(() => {
    if (!classSearch.trim()) return allClasses;
    const q = classSearch.toLowerCase();
    return allClasses.filter((e: any) =>
      e.class_code?.toLowerCase().includes(q) ||
      e.school_name?.toLowerCase().includes(q) ||
      e.professor_name?.toLowerCase().includes(q)
    );
  }, [allClasses, classSearch]);

  const getSessionContext = (s: StudySession & { contextName?: string }) => {
    if (s.contextName) return s.contextName;
    if (s.classSectionId) {
      const enrollment = enrollments?.enrollments?.find((e: any) => e.id === s.classSectionId);
      return enrollment ? enrollment.class_code : 'Class';
    }
    if (s.examSectionId) return 'Exam';
    return 'Study';
  };

  const resumeSession = (s: StudySession) => {
    const mode = (s.filters as any)?.mode;
    if (s.classSectionId && mode === 'flashcard') navigate(`/flashcards/${s.classSectionId}?session=${s.id}`);
    else if (s.classSectionId && mode === 'quick') navigate(`/quickmem/${s.classSectionId}?session=${s.id}`);
    else if (s.classSectionId) navigate(`/study/class/${s.classSectionId}?session=${s.id}`);
    else if (s.examSectionId) navigate(`/study/exam/${s.examSectionId}?session=${s.id}`);
  };

  const firstName = me?.user?.firstName;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <PageTransition className="space-y-6">
      {/* ── Greeting ───────────────────────────────────────────────── */}
      <FadeIn>
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">{greeting}</p>
            <h1 className="font-display text-2xl text-squishy-navy leading-tight truncate">
              {firstName ?? 'Welcome back'}
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              {dailyDone ? "Goal crushed. You're on a roll." : streak > 0 ? `Keep that ${streak} day streak alive.` : "Let's make today count."}
            </p>
          </div>
          {streak > 0 && (
            <div className="shrink-0 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full pl-2.5 pr-3 py-1.5">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-squishy-gold" aria-hidden>
                <path d="M12 2c.6 3.2-.6 5.1-2 6.6C8.4 10.3 7 11.9 7 14.5A5.5 5.5 0 0 0 12.5 20 5.5 5.5 0 0 0 18 14.5c0-1.5-.5-2.8-1.3-4-.5 1-1.2 1.7-2.2 2 .3-2.9-.6-7.2-2.5-10.5z" />
              </svg>
              <span className="text-xs font-bold text-amber-700">
                <CountUp value={streak} /> day{streak === 1 ? '' : 's'}
              </span>
            </div>
          )}
        </div>
      </FadeIn>

      {/* ── Daily goal + shares card ───────────────────────────────── */}
      <FadeIn delay={0.05}>
        <section className="bg-white border border-slate-100 rounded-3xl shadow-card px-4 py-3.5">
          <div className="flex items-center gap-3">
            {/* Daily goal ring */}
            <button
              onClick={() => togglePanel('daily')}
              aria-expanded={expandedPanel === 'daily'}
              className="flex items-center gap-3 flex-1 min-w-0 text-left rounded-2xl active:opacity-80 transition-opacity min-h-[44px]"
            >
              <GoalRing done={dailyDone} value={todayDone} percent={dailyProgress} />
              <div className="min-w-0">
                <div className="text-sm font-bold text-squishy-navy">Daily goal</div>
                <div className="text-xs text-slate-400">
                  {dailyDone ? 'Complete' : `${dailyGoal - todayDone} to go`}
                </div>
              </div>
              <Chevron open={expandedPanel === 'daily'} />
            </button>

            <div className="w-px self-stretch bg-slate-100 my-1" aria-hidden />

            {/* Shared */}
            <button
              onClick={() => togglePanel('shares')}
              aria-expanded={expandedPanel === 'shares'}
              className="flex items-center gap-2.5 shrink-0 text-left rounded-2xl active:opacity-80 transition-opacity min-h-[44px] pl-1"
            >
              <div>
                <div className="text-lg font-bold text-squishy-navy leading-none">
                  <CountUp value={totalShares} />
                </div>
                <div className="text-[11px] text-slate-400 mt-1">shared</div>
              </div>
              <Chevron open={expandedPanel === 'shares'} />
            </button>
          </div>

          {/* Expandable panels */}
          <ExpandPanel open={expandedPanel === 'daily'}>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold text-squishy-navy">Today's goal</span>
                <span className="text-xs text-slate-500">{todayDone} / {dailyGoal}</span>
              </div>
              <div className="mt-2 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${dailyDone ? 'bg-emerald-400' : 'bg-squishy-teal'}`}
                  initial={false}
                  animate={{ width: `${dailyProgress}%` }}
                  transition={springs.soft}
                />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                {todayDone === 0 ? `Answer ${dailyGoal} questions to keep your streak alive.`
                  : dailyDone ? `Goal crushed! You answered ${todayDone} questions today.`
                  : `${dailyGoal - todayDone} to go. You've got this.`}
              </p>
            </div>
          </ExpandPanel>

          <ExpandPanel open={expandedPanel === 'shares'}>
            <div className="mt-3 rounded-2xl bg-gradient-to-br from-squishy-navy to-squishy-purple p-4 text-white">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-bold">Questions shared</span>
                <span className="text-xs opacity-75">{totalShares} total</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="bg-white/15 rounded-xl py-2.5">
                  <div className="text-lg font-bold"><CountUp value={sharedToday} /></div>
                  <div className="text-[10px] opacity-70 mt-0.5">Today</div>
                </div>
                <div className="bg-white/15 rounded-xl py-2.5">
                  <div className="text-lg font-bold"><CountUp value={totalAnswers} /></div>
                  <div className="text-[10px] opacity-70 mt-0.5">Answered</div>
                </div>
                <div className="bg-white/15 rounded-xl py-2.5">
                  <div className="text-lg font-bold"><CountUp value={correctPct} format={(n) => `${n}%`} /></div>
                  <div className="text-[10px] opacity-70 mt-0.5">Correct</div>
                </div>
              </div>
            </div>
          </ExpandPanel>
        </section>
      </FadeIn>

      {/* ── Resume where you left off ────────────────────────────── */}
      {sessions.length > 0 && (
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-lg text-squishy-navy">Pick up where you left off</h2>
            {sessions.length > 2 && (
              <button
                onClick={() => setShowAllSessions(true)}
                className="text-xs font-semibold text-squishy-tealDark hover:underline min-h-[44px] -my-3 px-1"
              >
                View all
              </button>
            )}
          </div>
          <div className="overflow-x-auto no-scrollbar -mx-4 px-4" style={{ WebkitOverflowScrolling: 'touch' }}>
            <Stagger className="inline-flex gap-3 pb-1">
              {sessions.slice(0, 6).map((s) => (
                <Stagger.Item key={s.id} className="shrink-0">
                  <SessionCard
                    session={s}
                    contextName={getSessionContext(s)}
                    onResume={() => resumeSession(s)}
                    onDiscard={() => setConfirmDeleteId(s.id)}
                  />
                </Stagger.Item>
              ))}
            </Stagger>
          </div>
        </section>
      )}

      {/* Fallback CTA if no sessions */}
      {sessions.length === 0 && hasClasses && (
        <FadeIn delay={0.1}>
          <Link
            to={`/class/${enrollments!.enrollments[0].id}`}
            className="group flex items-center justify-between rounded-3xl p-5 text-white shadow-pop bg-gradient-to-br from-squishy-navy to-squishy-tealDark active:scale-[0.98] transition-transform"
          >
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-widest opacity-75 font-semibold">Start studying</div>
              <div className="font-display text-xl mt-1 truncate">
                {enrollments!.enrollments[0].class_code}
              </div>
            </div>
            <span className="shrink-0 w-10 h-10 rounded-full bg-white/15 flex items-center justify-center group-hover:bg-white/25 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </span>
          </Link>
        </FadeIn>
      )}

      {/* ── My Classes ─────────────────────────────────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg text-squishy-navy">My classes</h2>
          <Link
            to="/onboarding"
            className="inline-flex items-center gap-1.5 bg-gradient-to-r from-squishy-tealDark to-teal-600 text-white pl-3.5 pr-4 rounded-xl text-sm font-semibold shadow-card hover:shadow-pop transition-shadow active:scale-[0.97] min-h-[44px]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            Add Class
          </Link>
        </div>

        {/* Search bar (show if 4+ classes) */}
        {allClasses.length >= 4 && (
          <div className="relative mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              value={classSearch}
              onChange={(e) => setClassSearch(e.target.value)}
              placeholder="Search your classes..."
              className="w-full bg-white border border-slate-200 rounded-2xl pl-10 pr-3 py-3 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:border-squishy-teal focus:shadow-glow transition-shadow"
            />
          </div>
        )}

        {filteredClasses.length > 0 ? (
          <Stagger className="space-y-2.5">
            {filteredClasses.map((e: any) => (
              <Stagger.Item key={e.id}>
                <div className="flex items-center gap-2 bg-white border border-slate-100 rounded-2xl p-3 shadow-card hover:shadow-pop transition-shadow group">
                  <Link
                    to={`/class/${e.id}`}
                    className="flex items-center gap-3 flex-1 min-w-0 active:scale-[0.99] transition-transform"
                  >
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-squishy-navy to-squishy-purple flex items-center justify-center text-white text-[11px] font-bold shrink-0 tracking-tight">
                      {e.class_code?.substring(0, 4)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-squishy-navy">{e.class_code}</div>
                      <div className="text-xs text-slate-400 truncate">
                        {e.school_name}{e.professor_name ? ` · Prof. ${e.professor_name}` : ''}{e.term ? ` · ${e.term}` : ''}
                      </div>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-slate-300 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                  </Link>
                  <AnimatePresence mode="popLayout" initial={false}>
                    {confirmUnenrollId === e.id ? (
                      <motion.div
                        key="confirm"
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        transition={springs.snappy}
                        className="flex items-center gap-1.5 shrink-0"
                      >
                        <Pressable
                          haptic="warning"
                          onClick={() => { unenroll.mutate(e.id); setConfirmUnenrollId(null); }}
                          className="text-xs font-semibold text-red-600 bg-red-50 px-3 min-h-[44px] rounded-xl hover:bg-red-100 transition-colors"
                        >
                          Remove
                        </Pressable>
                        <Pressable
                          onClick={() => setConfirmUnenrollId(null)}
                          className="text-xs font-semibold text-slate-500 bg-slate-100 px-3 min-h-[44px] rounded-xl hover:bg-slate-200 transition-colors"
                        >
                          Cancel
                        </Pressable>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="x"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={springs.snappy}
                        className="shrink-0"
                      >
                        <Pressable
                          onClick={() => setConfirmUnenrollId(e.id)}
                          className="w-11 h-11 flex items-center justify-center text-slate-300 hover:text-red-400 transition-colors opacity-60 group-hover:opacity-100"
                          title="Remove class"
                          aria-label={`Remove ${e.class_code}`}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        </Pressable>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Stagger.Item>
            ))}
          </Stagger>
        ) : allClasses.length > 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No classes match &ldquo;{classSearch}&rdquo;</p>
        ) : (
          <FadeIn>
            <Link
              to="/onboarding"
              className="flex flex-col items-center gap-2 border-2 border-dashed border-slate-200 rounded-3xl px-6 py-8 text-center hover:border-squishy-teal transition-colors group"
            >
              <span className="w-11 h-11 rounded-full bg-slate-100 group-hover:bg-teal-50 flex items-center justify-center text-slate-400 group-hover:text-squishy-tealDark transition-colors">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </span>
              <span className="text-sm font-semibold text-slate-500 group-hover:text-squishy-tealDark transition-colors">Add your first class</span>
              <span className="text-xs text-slate-400">Find your school and course in seconds</span>
            </Link>
          </FadeIn>
        )}
      </section>

      {/* ── All Sessions Overlay ──────────────────────────────────── */}
      <AnimatePresence>
        {showAllSessions && (
          <AllSessionsOverlay
            sessions={sessions}
            getContext={getSessionContext}
            onResume={resumeSession}
            onDiscard={(id) => setConfirmDeleteId(id)}
            onClose={() => setShowAllSessions(false)}
          />
        )}
      </AnimatePresence>

      {/* ── Delete Session Confirmation ─────────────────────────── */}
      <Modal open={Boolean(confirmDeleteId)} onClose={() => setConfirmDeleteId(null)}>
        <div className="bg-white rounded-3xl p-6 shadow-pop text-center space-y-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
            <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </div>
          <div>
            <p className="text-base font-bold text-squishy-navy">Discard this session?</p>
            <p className="text-xs text-slate-500 mt-1">Your progress will be lost and can't be recovered.</p>
          </div>
          <div className="flex gap-2">
            <Pressable
              onClick={() => setConfirmDeleteId(null)}
              className="flex-1 py-3 rounded-2xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Keep
            </Pressable>
            <Pressable
              haptic="warning"
              onClick={handleConfirmDelete}
              className="flex-1 py-3 rounded-2xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors"
            >
              Discard
            </Pressable>
          </div>
        </div>
      </Modal>

      {/* ── New User Welcome Modal ───────────────────────────────── */}
      <Modal open={Boolean(showWelcome)} onClose={() => setDismissedWelcome(true)}>
        <div className="bg-white rounded-3xl p-6 shadow-pop text-center space-y-5">
          <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-squishy-teal to-squishy-tealDark shadow-soft flex items-center justify-center">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
            </svg>
          </div>
          <div>
            <h2 className="font-display text-2xl text-squishy-navy">Welcome to Squishy!</h2>
            <p className="text-sm text-slate-500 mt-1.5">What would you like to start with?</p>
          </div>
          <div className="space-y-3">
            <Pressable
              haptic="medium"
              onClick={() => navigate('/onboarding')}
              className="w-full bg-gradient-to-br from-squishy-tealDark to-teal-600 text-white py-4 rounded-2xl font-bold text-base shadow-card text-center"
            >
              A Class
              <span className="block text-xs font-normal opacity-80 mt-0.5">Add your school &amp; class</span>
            </Pressable>
            <Pressable
              haptic="medium"
              onClick={() => navigate('/exams')}
              className="w-full bg-gradient-to-br from-squishy-navy to-squishy-purple text-white py-4 rounded-2xl font-bold text-base shadow-card text-center"
            >
              A Professional Exam
              <span className="block text-xs font-normal opacity-80 mt-0.5">NCLEX, MCAT, USMLE &amp; more</span>
            </Pressable>
          </div>
          <button
            onClick={() => setDismissedWelcome(true)}
            className="text-xs text-slate-400 hover:text-slate-600 min-h-[44px] px-4"
          >
            Skip for now
          </button>
        </div>
      </Modal>
    </PageTransition>
  );
}

// ── Daily goal ring ─────────────────────────────────────────────────────────

function GoalRing({ done, value, percent }: { done: boolean; value: number; percent: number }) {
  const R = 26;
  const C = 2 * Math.PI * R;
  return (
    <div className="relative w-14 h-14 shrink-0">
      <svg className="w-14 h-14 -rotate-90" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={R} fill="none" stroke="#f1f5f9" strokeWidth="6" />
        <motion.circle
          cx="32" cy="32" r={R} fill="none"
          stroke={done ? '#2dd4bf' : '#0d9488'}
          strokeWidth="6" strokeLinecap="round"
          strokeDasharray={C}
          initial={false}
          animate={{ strokeDashoffset: C - (C * percent) / 100 }}
          transition={springs.soft}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {done ? (
          <motion.span
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={springs.bouncy}
            className="flex"
          >
            <svg className="w-5 h-5 text-squishy-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </motion.span>
        ) : (
          <CountUp value={value} className="text-sm font-bold text-squishy-tealDark" />
        )}
      </div>
    </div>
  );
}

// ── Chevron indicator ───────────────────────────────────────────────────────

function Chevron({ open }: { open: boolean }) {
  return (
    <motion.svg
      className="w-3.5 h-3.5 text-slate-300 shrink-0"
      viewBox="0 0 20 20" fill="currentColor"
      animate={{ rotate: open ? 180 : 0 }}
      transition={springs.snappy}
      aria-hidden
    >
      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
    </motion.svg>
  );
}

// ── Smooth expand/collapse panel ────────────────────────────────────────────

function ExpandPanel({ open, children }: { open: boolean; children: React.ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          className="overflow-hidden"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── Session Card ────────────────────────────────────────────────────────────

function SessionCard({ session, contextName, onResume, onDiscard }: {
  session: StudySession;
  contextName: string;
  onResume: () => void;
  onDiscard: () => void;
}) {
  const progress = session.totalQuestions > 0
    ? Math.round((session.questionsAnswered / session.totalQuestions) * 100)
    : 0;
  const title = session.label || 'All Topics';

  return (
    <motion.div
      role="button"
      tabIndex={0}
      onClick={() => { haptic.light(); onResume(); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onResume(); } }}
      whileTap={{ scale: 0.96 }}
      transition={springs.snappy}
      className="w-44 rounded-2xl bg-gradient-to-br from-squishy-teal to-squishy-tealDark shadow-pop p-3.5 flex flex-col gap-1.5 cursor-pointer relative select-none overflow-hidden"
    >
      <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10" aria-hidden />
      <button
        onClick={(e) => { e.stopPropagation(); onDiscard(); }}
        className="absolute top-0 right-0 w-11 h-11 flex items-center justify-center text-white/60 hover:text-white transition-colors"
        aria-label="Discard session"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>
      <span className="self-start inline-block text-[10px] font-bold uppercase tracking-wider text-white bg-white/20 rounded-full px-2 py-0.5 max-w-[7.5rem] truncate">
        {contextName}
      </span>
      <div className="text-sm font-bold text-white truncate pr-4">{title}</div>
      <div className="h-1.5 bg-white/25 rounded-full overflow-hidden mt-0.5">
        <motion.div
          className="h-full bg-white rounded-full"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={springs.soft}
        />
      </div>
      <div className="text-[11px] text-white/80 font-medium">
        {progress}% · {session.questionsAnswered}/{session.totalQuestions}
      </div>
    </motion.div>
  );
}

// ── All Sessions Overlay ────────────────────────────────────────────────────

function AllSessionsOverlay({ sessions, getContext, onResume, onDiscard, onClose }: {
  sessions: StudySession[];
  getContext: (s: StudySession) => string;
  onResume: (s: StudySession) => void;
  onDiscard: (id: string) => void;
  onClose: () => void;
}) {
  return createPortal(
    <motion.div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
      onClick={onClose}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="absolute inset-x-0 bottom-0 top-12 bg-white rounded-t-3xl p-5 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={springs.soft}
      >
        <div className="mx-auto w-10 h-1 rounded-full bg-slate-200 mb-4" aria-hidden />
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg text-squishy-navy">All sessions</h2>
          <Pressable
            onClick={onClose}
            className="w-11 h-11 -mr-2 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
            </svg>
          </Pressable>
        </div>
        <Stagger className="space-y-2.5">
          {sessions.map((s) => {
            const progress = s.totalQuestions > 0
              ? Math.round((s.questionsAnswered / s.totalQuestions) * 100)
              : 0;
            const title = s.label || `${s.totalQuestions} questions`;
            return (
              <Stagger.Item key={s.id}>
                <div className="border border-slate-100 rounded-2xl p-4 flex items-center gap-3 shadow-card bg-white">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-squishy-navy truncate">{title}</div>
                    <div className="text-xs text-slate-500">{getContext(s)}</div>
                    <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-squishy-tealDark to-squishy-teal rounded-full" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">{progress}% · {s.questionsAnswered}/{s.totalQuestions} · {s.xpEarned} XP</div>
                  </div>
                  <Pressable
                    onClick={() => onResume(s)}
                    className="shrink-0 text-xs font-semibold min-h-[44px] px-4 rounded-xl bg-squishy-tealDark text-white hover:opacity-90 transition-opacity"
                  >
                    Resume
                  </Pressable>
                  <Pressable
                    onClick={() => onDiscard(s.id)}
                    className="shrink-0 w-11 h-11 flex items-center justify-center text-slate-300 hover:text-red-400 transition-colors"
                    aria-label="Discard session"
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                      <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                    </svg>
                  </Pressable>
                </div>
              </Stagger.Item>
            );
          })}
        </Stagger>
      </motion.div>
    </motion.div>,
    document.body,
  );
}
